import {useCallback, useContext, useEffect, useState} from 'react';

import {MessageContext} from 'src/contexts/message-context';
import {getConnectionErrorMessage} from 'src/modules/profile/account/tabs/license-and-features/crm-tab/utils/getDuplicatesErrorMessage';
import {
    checkSalesforceCredentials,
    cleanupOAuthCredentials,
    getSalesforceCredentials,
    saveSalesforceCredentials,
    unlinkSalesforceOrgId,
} from 'src/openapi/admin/apis/SalesforceControllerApi';
import {CredentialsType as CredentialsTypes} from 'src/openapi/admin/models/CredentialsType';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';
import {checkIsChanged} from 'src/utils/use-case-utils';

import {isSalesforceTestReady} from './utils/isSalesforceTestReady';

import type {CredentialsUpdateResult} from 'src/openapi/admin/models/CredentialsUpdateResult';
import type {Error} from 'src/openapi/admin/models/Error';
import type {SalesforceCredentials} from 'src/openapi/admin/models/SalesforceCredentials';

const DEFAULT_SALESFORCE_CREDENTIALS: SalesforceCredentials = {
    passwordCredentials: {login: '', password: '', passwordChanged: false, passwordNotEmpty: false},
    credentialsType: CredentialsTypes.PASSWORD,
    securityToken: '',
    sandbox: false,
    refreshTokenNotEmpty: false,
    authEndpoint: '',
};

type Props = {
    accountId: number,
    isCrmEnabled: boolean,
};

export const useSalesforceCredentials = (props: Props) => {
    const {accountId, isCrmEnabled} = props;
    const apiDispatch = useAppDispatch();
    const {setErrorMessage} = useContext(MessageContext);

    const [isLoading, setIsLoading] = useState(true);
    const [isConnectionStatusLoading, setIsConnectionStatusLoading] = useState(false);
    const [isUnlinkBusy, setUnlinkBusy] = useState(false);
    const [loadingError, setLoadingError] = useState<Error | null>(null);

    const [initialData, setInitialData] = useState<SalesforceCredentials | null>(null);
    const [data, setData] = useState<SalesforceCredentials>(DEFAULT_SALESFORCE_CREDENTIALS);
    const [connectionStatus, setConnectionStatus] = useState<CredentialsUpdateResult | null>(null);

    const testConnection = useCallback(async (salesforceCredentials: SalesforceCredentials) => {
        setIsConnectionStatusLoading(true);
        const testCredentialsResponse = await apiDispatch(checkSalesforceCredentials({
            accountId,
            salesforceCredentials,
        }));

        if (testCredentialsResponse.payload.success) {
            setConnectionStatus(testCredentialsResponse.payload.data);
        } else {
            setErrorMessage(getError({error: testCredentialsResponse.payload.data}));
        }
        setIsConnectionStatusLoading(false);
    }, [accountId, apiDispatch, setErrorMessage]);

    const discardChanges = useCallback(() => {
        if (initialData) {
            setData(initialData);
            testConnection(initialData);
        }
    }, [initialData, testConnection]);

    useEffect(() => {
        const loadSalesforceCredentials = async () => {
            setIsLoading(true);
            const salesforceCredentialsResponse = await apiDispatch(getSalesforceCredentials({accountId}));
            if (salesforceCredentialsResponse.payload.success) {
                setInitialData(salesforceCredentialsResponse.payload.data);
                setData(salesforceCredentialsResponse.payload.data);

                if (isSalesforceTestReady(salesforceCredentialsResponse.payload.data) && isCrmEnabled) {
                    testConnection(salesforceCredentialsResponse.payload.data);
                }
            } else {
                setLoadingError(salesforceCredentialsResponse.payload.data);
            }
            setIsLoading(false);
        };

        loadSalesforceCredentials();
    }, [accountId, isCrmEnabled, apiDispatch, testConnection]);

    const saveChanges = useCallback(async () => {
        let error: string | null = null;

        setIsConnectionStatusLoading(true);
        const saveSalesforceCredentialsResponse = await apiDispatch(saveSalesforceCredentials({
            accountId,
            salesforceCredentials: data,
        }));
        setIsConnectionStatusLoading(false);

        if (saveSalesforceCredentialsResponse.payload.error) {
            error = getError({error: saveSalesforceCredentialsResponse.payload.data, separator: ' '});
        } else if (saveSalesforceCredentialsResponse.payload.data.type?.code === 'dup') {
            error = getConnectionErrorMessage(saveSalesforceCredentialsResponse.payload.data);
        } else {
            setInitialData(data);
            if (isSalesforceTestReady(data)) {
                setConnectionStatus(saveSalesforceCredentialsResponse.payload.data);
            } else {
                setConnectionStatus(null);
            }
        }

        return error;
    }, [accountId, data, apiDispatch]);

    const unlinkConnection = useCallback(async () => {
        setUnlinkBusy(true);
        const salesforceUnlinkResponse = await apiDispatch(unlinkSalesforceOrgId({accountId}));
        if (salesforceUnlinkResponse.payload.success) {
            setConnectionStatus(null);
        } else {
            setErrorMessage(getError({error: salesforceUnlinkResponse.payload.data}));
        }
        setUnlinkBusy(false);
    }, [accountId, apiDispatch, setErrorMessage]);

    const revokeConnection = useCallback(async () => {
        const salesforceRevokeResponse = await apiDispatch(
            cleanupOAuthCredentials({accountId}),
        );
        if (salesforceRevokeResponse.payload.success) {
            setConnectionStatus(null);
        } else {
            setErrorMessage(getError({error: salesforceRevokeResponse.payload.data}));
        }
    }, [accountId, apiDispatch, setErrorMessage]);

    return {
        isLoading,
        isConnectionStatusLoading,
        isUnlinkBusy,
        loadingError,
        isChanged: checkIsChanged(initialData, data),

        data, setData,
        connectionStatus,

        saveChanges,
        unlinkConnection,
        revokeConnection,
        testConnection,
        discardChanges,
    };
};
